import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, FileUp, Loader2, Sparkles } from "lucide-react";

const CONTRACTS = ["Alternance", "CDI", "CDD", "Stage", "Premier emploi"];

const DEMO_CV = `Prénom Nom – Étudiant(e) BTS MCO (Management Commercial Opérationnel)
Email : demo@alternai.fr · Tél : 06 12 34 56 78 · Paris

PROFIL
Étudiant(e) en 2e année de BTS MCO, à la recherche d'une alternance en vente/relation client. Motivé(e), organisé(e), à l'aise avec le contact client et le travail en équipe.

FORMATION
- 2024–2026 : BTS MCO — Lycée exemple, Paris
- 2024 : Baccalauréat général, mention bien

EXPÉRIENCES
- 2023 (6 mois) : Conseiller(ère) de vente — Boutique de prêt-à-porter, Paris
  Accueil client, conseil, encaissement, mise en rayon, réassort. Taux de satisfaction client suivi hebdomadairement.
- 2022 (2 mois) : Stage vente — Enseigne de sport, Paris
  Merchandising, tenue de rayon, support opérations promotionnelles.

COMPÉTENCES
Relation client, techniques de vente, encaissement, merchandising, gestion des stocks, Pack Office, esprit d'équipe.

LANGUES
Français (natif), Anglais (B2), Espagnol (A2).`;

const DEMO_OFFER = `Nous recherchons un(e) Conseiller(ère) de vente en alternance pour notre boutique parisienne.
Missions : accueil et conseil client, vente additionnelle, encaissement, tenue du magasin, réassort, participation aux opérations commerciales.
Profil : formation Bac+2 en cours (MCO, NDRC ou équivalent), sens du service, bonne présentation, esprit d'équipe, dynamisme.
Contrat : Alternance 12 à 24 mois. Rythme : 2 jours école / 3 jours entreprise.`;

export default function NewApplication() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1);
  const [contractType, setContractType] = useState("");
  const [cvText, setCvText] = useState("");
  const [company, setCompany] = useState("");
  const [position, setPosition] = useState("");
  const [location, setLocation] = useState("");
  const [offer, setOffer] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les PDF sont supportés. Utilise le copier-coller sinon.");
      return;
    }
    setExtracting(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/cv/extract", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCvText(data.text);
      toast.success(`CV importé (${data.pages} page${data.pages > 1 ? "s" : ""}).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction PDF impossible.");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadDemo = () => {
    setContractType("Alternance");
    setCvText(DEMO_CV);
    setOffer(DEMO_OFFER);
    setCompany("Boutique Démo");
    setPosition("Conseiller(ère) de vente");
    setLocation("Paris");
    toast.success("Données de démonstration chargées.");
  };

  const next = () => {
    if (step === 1 && !contractType) return toast.error("Sélectionne un type de candidature.");
    if (step === 2 && cvText.trim().length < 50) return toast.error("Le CV semble trop court.");
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (offer.trim().length < 30) {
      toast.error("Colle une offre plus complète.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", {
        contract_type: contractType,
        cv_text: cvText,
        job_offer: offer,
        company: company || null,
        position: position || null,
        location: location || null,
      });
      toast.success("Analyse terminée !");
      navigate(`/candidature/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analyse impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Nav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-jakarta text-2xl sm:text-3xl font-bold text-slate-900">Nouvelle candidature</h1>
          <Button variant="ghost" onClick={loadDemo} className="text-blue-600 hover:text-blue-700" data-testid="load-demo-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> Charger la démo
          </Button>
        </div>

        {/* Stepper */}
        <div className="mt-6 flex items-center gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={`h-8 w-8 grid place-items-center rounded-full text-sm font-semibold ${step >= n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > n ? <Check className="h-4 w-4" /> : n}
              </div>
              <div className={`flex-1 h-0.5 ${step > n ? "bg-blue-600" : "bg-slate-200"}`} />
            </div>
          ))}
        </div>

        <Card className="mt-6 p-6 sm:p-8 rounded-2xl border-slate-200 bg-white shadow-sm">
          {step === 1 && (
            <div>
              <h2 className="font-jakarta text-xl font-semibold text-slate-900">Quel type de candidature ?</h2>
              <p className="mt-1 text-sm text-slate-500">Choisis le type de contrat visé.</p>
              <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {CONTRACTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setContractType(c)}
                    data-testid={`contract-${c.toLowerCase().replace(/\s/g, "-")}`}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${
                      contractType === c
                        ? "border-blue-600 bg-blue-50/70"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className="font-jakarta font-semibold text-slate-900">{c}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {c === "Alternance" && "Étudier + travailler en entreprise."}
                      {c === "CDI" && "Poste durable à temps plein."}
                      {c === "CDD" && "Mission à durée déterminée."}
                      {c === "Stage" && "Immersion pédagogique."}
                      {c === "Premier emploi" && "Ta première expérience pro."}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-jakarta text-xl font-semibold text-slate-900">Ton CV</h2>
              <p className="mt-1 text-sm text-slate-500">Importe un PDF ou colle simplement son contenu.</p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button onClick={onPickFile} disabled={extracting} variant="outline" className="rounded-full h-11" data-testid="cv-upload-btn">
                  {extracting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileUp className="h-4 w-4 mr-1.5" />}
                  {extracting ? "Extraction…" : "Importer un PDF"}
                </Button>
                <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} className="hidden" data-testid="cv-file-input" />
                <div className="text-sm text-slate-400 self-center">ou colle le contenu ci-dessous</div>
              </div>

              <Textarea
                data-testid="cv-textarea"
                value={cvText}
                onChange={(e) => setCvText(e.target.value)}
                placeholder="Colle ici le contenu de ton CV : expériences, formation, compétences…"
                className="mt-4 min-h-[280px] text-sm leading-relaxed"
              />
              <div className="mt-2 text-xs text-slate-400">{cvText.length} caractères</div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-jakarta text-xl font-semibold text-slate-900">L'offre d'emploi</h2>
              <p className="mt-1 text-sm text-slate-500">Colle l'offre. Ajoute les infos optionnelles pour une meilleure personnalisation.</p>

              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-700">Entreprise</Label>
                  <Input data-testid="offer-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Ex : Decathlon" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-slate-700">Poste</Label>
                  <Input data-testid="offer-position" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Ex : Conseiller(ère)" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-slate-700">Ville</Label>
                  <Input data-testid="offer-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex : Paris" className="mt-1.5" />
                </div>
              </div>

              <Textarea
                data-testid="offer-textarea"
                value={offer}
                onChange={(e) => setOffer(e.target.value)}
                placeholder="Colle ici le texte complet de l'offre d'emploi…"
                className="mt-4 min-h-[240px] text-sm leading-relaxed"
              />

              <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                Score indicatif basé uniquement sur les informations fournies. Il ne
                garantit pas l'obtention d'un entretien ou d'un emploi. L'IA n'inventera
                jamais d'expérience ou de compétence absente du CV.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={step === 1 ? () => navigate("/dashboard") : back}
              className="text-slate-500"
              data-testid="wizard-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {step === 1 ? "Retour au dashboard" : "Précédent"}
            </Button>

            {step < 3 ? (
              <Button onClick={next} className="bg-blue-600 hover:bg-blue-700 rounded-full px-6 h-11" data-testid="wizard-next">
                Continuer <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 rounded-full px-6 h-11" data-testid="wizard-analyze">
                {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analyse…</> : <>Analyser ma candidature <ArrowRight className="h-4 w-4 ml-1.5" /></>}
              </Button>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
